import { FC, useEffect, useRef, useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { useGeneratePromptToShow } from '@/hooks/promptCanvas/useGeneratePromptToShow';
import { cn } from '@/utils/tailwind';

import InputBar from './InputBar';
import { getSelectedTextRange } from './utils';

type TProps = {
  isMutating: boolean;
  isFirstTokenReceived: boolean;
  promptToShow: string;
  onSubmit: (
    metaprompt: string,
    highlightedRange: [number, number] | null
  ) => void;
  handleManualPromptUpdate: (prompt: string) => void;
  showDiff: boolean;
  previousPrompt?: string;
};

const PromptCanvas: FC<TProps> = ({
  promptToShow,
  onSubmit,
  handleManualPromptUpdate,
  isMutating,
  isFirstTokenReceived,
  showDiff,
  previousPrompt,
}) => {
  const highlightedRangeRef = useRef<[number, number] | null>(null);
  const promptRef = useRef<HTMLDivElement>(null);

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const [metaprompt, setMetaprompt] = useState<string>('');
  const isStale = useRef(false);

  const [showManualHighlight, setShowManualHighlight] = useState(false);

  const resetState = () => {
    setMetaprompt('');
    setShowManualHighlight(false);
    setIsPopoverOpen(false);
    highlightedRangeRef.current = null;
  };

  // todo: support keyboard based selection
  const handleMouseDown = (target: Node) => {
    if (showDiff) return;
    if (isStale.current && textRef.current?.innerText) {
      handleManualPromptUpdate(textRef.current.innerText);
      isStale.current = false;
    }
    // reset highlighted range if we are clicking outside the popover
    if (
      !popoverRef.current?.contains(target) &&
      !!highlightedRangeRef.current
    ) {
      resetState();
    }
  };

  useEffect(() => {
    const handleMouseUp = async (e) => {
      if (showDiff) return;
      const selectedRange = await getSelectedTextRange(promptRef);
      // prefer the mouseDown handler but included here as fallback to handle clicks outside this component
      if (
        !popoverRef.current?.contains(e.target) &&
        !!highlightedRangeRef.current
      ) {
        resetState();
      } else if (selectedRange) {
        const { startIdx, endIdx } = selectedRange;
        resetState();
        highlightedRangeRef.current = [startIdx, endIdx];
        setIsPopoverOpen(true);
      }
    };

    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [showDiff]);

  const textRef = useRef<HTMLSpanElement>(null);

  const { renderedPrompt } = useGeneratePromptToShow({
    promptToShow,
    previousPrompt,
    highlightedRangeRef,
    showManualHighlight,
    isMutating,
    handleManualPromptUpdate,
    resetState,
    isStale,
    textRef,
    showDiff,
  });

  return (
    <>
      <div
        className="flex flex-col gap-1 overflow-auto whitespace-pre-line rounded-lg pt-3"
        ref={promptRef}
        onMouseDown={(e) => handleMouseDown(e.target as Node)}
      >
        <div
          className={cn(
            'flex flex-col gap-1 overflow-auto whitespace-pre-line rounded-lg pt-3 [&_*::selection]:bg-brand-secondary',
            isMutating && !isFirstTokenReceived && 'animate-pulse'
          )}
          ref={promptRef}
        >
          <span className="text-sm">{renderedPrompt}</span>
        </div>
      </div>
      <Popover open={isPopoverOpen && !isMutating}>
        <PopoverTrigger />
        <PopoverContent
          style={{ width: '400px' }}
          ref={popoverRef}
          // Prevent switching focus on open/close
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onMouseDown={() => {
            setShowManualHighlight(true);
          }}
        >
          <InputBar
            metaprompt={metaprompt}
            setMetaprompt={setMetaprompt}
            isMutating={false}
            onSubmit={() => {
              onSubmit(metaprompt, highlightedRangeRef.current);
              resetState();
            }}
            placeholder="Enter instructions for highlighted text"
          />
        </PopoverContent>
      </Popover>
    </>
  );
};

export default PromptCanvas;
